import { useLocation, useNavigate } from "@solidjs/router";
import {
  Accessor,
  Setter,
  createSignal,
  onMount,
  Show,
  Switch,
  Match,
} from "solid-js";
import { Product } from "~/server/types/search";
import { zIndexes } from "~/z_indexes";
import { BalancePill } from "./header_telescope_components/balance_pill";
import { ProfileDropdown } from "./header_telescope_components/profile_dropdown";
import { SearchOverlay } from "~/components/search/search_overlay";
import { useWebStoreContext } from "~/context/webstore_context";
import { getSearchResults } from "~/components/search/search";
import { config } from "~/data/config";
import { openModalV2 } from "~/shared_states/modal";
import { RedeemCodeModalTelescope } from "~/components/webstore_telescope/redeem_code_modal_telescope";
import { LogoutConfirmationModal } from "../logout_confirmation_modal";
import { openCoinsCreditedMessageTelescope } from "../welcome_message_telescope";
import {
  shouldDisableLogout,
  enableSingleBrandHomepage,
} from "~/data/partner_config";
import { PlusIcon } from "~/components/search/search_icons";

/**
 * this header will only be used for RNR portals
 * so we can assume the user is logged in
 */

function RedeemButton(props: { onClick: () => void }) {
  return (
    <button
      class="flex h-8 cursor-pointer items-center justify-center gap-1 rounded-full bg-background-inverted-primary px-3 text-title-6-semi-bold text-text-inverted-primary duration-200 hover:bg-background-inverted-secondary focus:bg-background-inverted-tertiary"
      onClick={props.onClick}
    >
      <PlusIcon />
      <span>Redeem</span>
    </button>
  );
}

export function HeaderTelescopeRnr() {
  const navigate = useNavigate();
  const location = useLocation();
  const webStoreContext = useWebStoreContext();

  const headerImageHref = config.paths.homePageUrl;
  const [isMobileSearchActive, setIsMobileSearchActive] = createSignal(false);
  const [isDesktopSearchActive, setIsDesktopSearchActive] = createSignal(false);

  const [isMenuOpen, setisMenuOpen] = createSignal(false);
  const [allBrands, setAllBrands] = createSignal<Product[]>(
    webStoreContext?.storeState.brands ?? []
  );

  const { isHeaderScrolledOut } = useWebStoreContext();

  // Handle scroll to show/hide redeem button
  onMount(async () => {
    // Load brands for search
    setAllBrands(
      webStoreContext?.storeState.brands ??
        (await getSearchResults(
          "",
          { filterOutInactive: false },
          { pageNo: 0, limit: 30 }
        ))
    );
  });

  const showSearchBarOnMobile = () => {
    // show search bar on mobile if on home page or category page
    return location.pathname === "/" || location.pathname.includes("category");
  };

  const showSearchBar = () => {
    // if the number of brands is greater than 20, show the search bar
    return webStoreContext?.storeState.brands?.length > 20;
  };

  const handleLogoClick = () => {
    navigate(headerImageHref);
  };

  const handleSearch = async (query: string): Promise<Product[]> => {
    return await getSearchResults(query, { filterOutInactive: false });
  };

  const isHomePage = () => {
    return location.pathname === "/";
  };

  const handleRedeemClick = () => {
    const isSingleBrand =
      webStoreContext.storeState.brands?.length === 1 &&
      enableSingleBrandHomepage(webStoreContext.getClientId());
    const singleBrandName = isSingleBrand
      ? webStoreContext.storeState.brands[0].voucherProduct.title
      : undefined;

    openModalV2(
      () => (
        <RedeemCodeModalTelescope
          userName={
            webStoreContext.storeState.v4HomePage?.user?.firstName ?? ""
          }
          refreshV4HomePage={webStoreContext.refreshV4HomePage}
          onSuccess={(amount) => {
            openCoinsCreditedMessageTelescope({
              coinsCredited: amount,
              userName:
                webStoreContext.storeState.v4HomePage?.user?.firstName ?? "",
              totalBalance: webStoreContext.storeState.coins ?? 0,
              singleBrandName,
            });
            // Success callback - can show success message if needed
          }}
        />
      ),
      {
        size: "small",
      }
    );
  };

  const getTncUrl = () => {
    return (
      webStoreContext.getPartnerConfigV3().termsAndConditionsUrl ??
      "https://www.myhubble.money/terms-and-conditions"
    );
  };

  const handleProfileAction = async (
    action: "my-gift-cards" | "help" | "terms" | "logout"
  ) => {
    switch (action) {
      case "my-gift-cards":
        navigate("/profile");
        break;
      case "help":
        webStoreContext.openContactSupport();
        break;
      case "terms":
        window.open(getTncUrl(), "_blank");
        break;
      case "logout":
        setisMenuOpen(false);
        openModalV2(
          () => (
            <LogoutConfirmationModal
              onLogout={() => {
                navigate("/login");
              }}
            />
          ),
          {
            size: "medium",
          }
        );
        break;
    }
  };

  const displayName = () => {
    return webStoreContext.storeState.v4HomePage?.user?.firstName || "";
  };
  const displayContact = () => {
    return (
      webStoreContext.storeState.v4HomePage?.user?.email ||
      webStoreContext.storeState.v4HomePage?.user?.phoneNumber ||
      ""
    );
  };
  const balance = () => {
    return webStoreContext.storeState.coins ?? 0;
  };

  const logoUrl = () => {
    return (
      webStoreContext.getPartnerConfigV3().logoUrl ??
      "https://s3.ap-south-1.amazonaws.com/assets.hubble/web-assets/hubble_full.svg"
    );
  };

  const configBgColor = () => {
    return webStoreContext.getPartnerConfigV3().headerBgColor;
  };

  const showHeaderBottomStroke = () => {
    return configBgColor() === undefined;
  };

  const forceLightTheme = () => {
    return configBgColor() !== undefined;
  };

  return (
    <>
      {/* Main Header */}
      <div
        class="sticky top-0 w-full bg-background-normal-primary"
        classList={{
          "telescope-light-theme": forceLightTheme(),
        }}
        style={{
          "z-index": zIndexes.header,
          "background-color": configBgColor(),
        }}
      >
        <div class="flex w-full flex-col items-center">
          {/* purpose for adding this div is for fixing -> "on navigation to other pages on ios, the page opens in scrolled state" */}
          <div
            class={`pointer-events-none absolute left-0  right-0 top-20  h-screen  opacity-0 `}
          />

          {/* Desktop Layout */}
          <div class="flex w-full flex-col items-center">
            <div class="flex w-full max-w-[1024px] items-center justify-between px-3 py-3">
              {/* Logo */}
              <div class="w-auto">
                <img
                  src={logoUrl()}
                  alt="Logo"
                  class="h-8 w-auto cursor-pointer object-contain"
                  onClick={handleLogoClick}
                />
              </div>

              {/* Search Bar - Centered */}
              <Show when={showSearchBar()}>
                <div class="absolute left-1/2 top-1/2 hidden w-[360px] -translate-x-1/2 -translate-y-1/2 sm:block">
                  <SearchOverlay
                    isActive={isDesktopSearchActive as Accessor<boolean>}
                    setIsActive={setIsDesktopSearchActive}
                    searchBrands={allBrands}
                    totalBrandsCount={400}
                    onSearch={handleSearch}
                    onBrandClick={(product) =>
                      navigate(`/buy-gift-card/${product.brandKey}`)
                    }
                  />
                </div>
              </Show>

              {/* Right Section */}
              <div class="flex items-center gap-2">
                <Show when={webStoreContext.getPartnerConfigV3().showCoins}>
                  <Switch>
                    {/* Show Redeem Button on scroll */}
                    <Match
                      when={
                        isHomePage() &&
                        !isHeaderScrolledOut() &&
                        webStoreContext.getPartnerConfigV3()
                          .allowCodeApplication
                      }
                    >
                      <RedeemButton onClick={handleRedeemClick} />
                    </Match>

                    {/* Show Balance Pill when not scrolled */}
                    <Match when={true}>
                      <BalancePill
                        balance={balance()}
                        showRedeemButton={
                          webStoreContext.getPartnerConfigV3()
                            .allowCodeApplication
                        }
                        onRedeemClick={handleRedeemClick}
                      />
                    </Match>
                  </Switch>
                </Show>

                <ProfileDropdown
                  showLogout={
                    !shouldDisableLogout(webStoreContext.getClientId())
                  }
                  showHelp={
                    webStoreContext.getPartnerConfigV3().showContactSupport !==
                    false
                  }
                  isOpen={isMenuOpen}
                  setIsOpen={setisMenuOpen}
                  userName={displayName()}
                  userContact={displayContact()}
                  onAction={handleProfileAction}
                />
              </div>
            </div>

            {/* Bottom stroke */}
            <Show when={showHeaderBottomStroke()}>
              <div
                class=" h-px w-full bg-stroke-1 "
                classList={{
                  "hidden sm:block ": showSearchBarOnMobile(),
                  block: !showSearchBarOnMobile(),
                }}
              />
            </Show>
          </div>

          {/* Mobile Layout */}
          <div
            class="w-full flex-col "
            classList={{
              "flex sm:hidden": showSearchBarOnMobile(),
              hidden: !showSearchBarOnMobile(),
            }}
          >
            <Show when={showSearchBar()}>
              <div class="flex w-full flex-col gap-3 p-3">
                {/* Second Row: Search Bar */}
                <SearchOverlay
                  isActive={isMobileSearchActive}
                  setIsActive={setIsMobileSearchActive}
                  searchBrands={allBrands}
                  totalBrandsCount={400}
                  onSearch={handleSearch}
                  onBrandClick={(product) =>
                    navigate(`/buy-gift-card/${product.brandKey}`)
                  }
                />
              </div>
            </Show>

            {/* Bottom stroke */}
            <Show when={showHeaderBottomStroke()}>
              <div class="h-px w-full bg-stroke-1" />
            </Show>
          </div>
        </div>
      </div>
    </>
  );
}
